import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft } from "lucide-react";
import {
  PersonalInfoSection,
  PersonalInfoData,
} from "@/components/sections/PersonalInfoSection";
import {
  CompanySelectorSection,
  CompanySelectorData,
} from "@/components/sections/CompanySelectorSection";
import { authLabels, commonButtonLabels } from "@/i18n";

interface RegistrationFormProps {
  onBack: () => void;
  onSubmit: (data: Record<string, unknown>) => void;
  isLoading?: boolean;
  error?: string;
  userType: "dealer" | "business";
  nameFromBankId?: boolean;
}

const initialPersonalInfo: PersonalInfoData = {
  username: "",
  email: "",
  phoneNumber: "",
  password: "",
  confirmPassword: "",
};

const initialCompanyInfo: CompanySelectorData = {
  companyId: undefined,
  company: undefined,
};

const RegistrationForm = ({
  onBack,
  onSubmit,
  isLoading = false,
  error,
  userType,
  nameFromBankId = false,
}: RegistrationFormProps) => {
  const authLabels_i18n = authLabels();
  const buttonLabels = commonButtonLabels();
  
  const [personalInfo, setPersonalInfo] = useState(initialPersonalInfo);
  const [personalValid, setPersonalValid] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(initialCompanyInfo);
  const [companyValid, setCompanyValid] = useState(false);
  const [message, setMessage] = useState("");

  const handlePersonalChange = useCallback(
    (data: PersonalInfoData, valid: boolean) => {
      setPersonalInfo(data);
      setPersonalValid(valid);
    },
    []
  );

  const handleCompanyChange = useCallback(
    (data: CompanySelectorData, valid: boolean) => {
      setCompanyInfo(data);
      setCompanyValid(valid);
    },
    []
  );

  const handleSubmit = () => {
    if (!personalValid || !companyValid) {
      const validationMessage =
        userType === "dealer"
          ? authLabels_i18n.forms.validation.fillAllFieldsPersonalAndDealerInfo
          : authLabels_i18n.forms.validation.fillAllFieldsPersonalAndBusinessInfo;
      alert(validationMessage);
      return;
    }
    onSubmit({
      ...personalInfo,
      companyId: companyInfo.companyId,
      message,
      type: userType,
    });
  };

  const getTitle = () => {
    return userType === "dealer"
      ? authLabels_i18n.forms.registerDealer
      : authLabels_i18n.forms.registerBusiness;
  };

  const getMessageId = () => {
    return userType === "dealer" ? "dealerMessage" : "businessMessage";
  };

  const getMessagePlaceholder = () => {
    return authLabels_i18n.forms.messagePlaceholder;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h3 className="text-lg font-semibold">{getTitle()}</h3>
      </div>

      <div className="space-y-6">
        <PersonalInfoSection
          value={personalInfo}
          onChange={handlePersonalChange}
          nameFromBankId={nameFromBankId}
        />

        <div className="space-y-4">
          <h4 className="text-base font-semibold">
            {userType === "dealer"
              ? authLabels_i18n.forms.dealerInformation
              : authLabels_i18n.forms.businessInformation}
            <span className="text-red-500 ml-1">*</span>
          </h4>
          <CompanySelectorSection
            value={companyInfo}
            onChange={handleCompanyChange}
          />
        </div>
      </div>

      <div className="space-y-2">
        <label htmlFor={getMessageId()}>{authLabels_i18n.forms.message}</label>
        <Textarea
          id={getMessageId()}
          placeholder={getMessagePlaceholder()}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={3}
        />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <p className="text-sm">{error}</p>
        </div>
      )}

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onBack} disabled={isLoading}>
          {buttonLabels.cancel}
        </Button>
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={!personalValid || !companyValid || isLoading}
        >
          {isLoading
            ? authLabels_i18n.buttons.sendingApplication
            : authLabels_i18n.buttons.sendApplication}
        </Button>
      </div>
    </div>
  );
};

export default RegistrationForm;
